// src/pages/Sales/SalesDocumentEditPage.jsx
// =============================================================================
// Sales document create/edit page — invoice-ready redesign foundation
// =============================================================================
//
// PURPOSE
// -------
// This page is no longer only a lightweight reception sales editor. It is now the
// frontend foundation for proper invoice preparation:
// - document details,
// - current seller data from Company Master Data,
// - buyer snapshot data,
// - payment and bank selection,
// - line-level discounts,
// - skonto / early-payment terms,
// - commercial lines,
// - VAT summary,
// - print/internal notes,
// - posted snapshot diagnostics.
//
// IMPORTANT SNAPSHOT RULE
// -----------------------
// Draft mode shows CURRENT Company Master Data as the seller preview. When the
// user posts the document, the backend copies seller/buyer/payment/discount/tax
// data into immutable snapshot JSON columns on the SalesDocument. Future invoice
// PDF must render those snapshots, not live Company Master Data.
//
// DISCOUNT / SKONTO RULE
// ----------------------
// Rabat is implemented as a line-level discount. This keeps VAT calculation safe
// because every discount belongs to exactly one VAT rate. Skonto is implemented
// as a payment term only: it is shown and saved on the document, but it does not
// reduce the invoice gross/VAT totals at issue time.
//
// FRONTEND RESPONSIBILITY BOUNDARY
// --------------------------------
// This page helps the user prepare a clean document. It does not create invoice
// numbers, it does not create stock movements and it does not freeze snapshots.
// All authoritative posting logic remains in salesDocumentService on the backend.
//
// I18N NOTE
// ---------
// User-facing labels are translated through the sales namespace. Technical enum
// values sent to the backend stay stable English codes such as invoice, receipt,
// cash, transfer, product, service, manual, draft and posted.

import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import SalesItemPicker from "../../components/catalog/pickers/SalesItemPicker";
import {
  createSalesDocument,
  fetchSalesDocumentById,
  postSalesDocument,
  searchSalesProducts,
  searchSalesServices,
  updateSalesDocument,
} from "../../services/salesService";
import { fetchStockBalances, fetchStockLocations } from "../../services/stockService";
import { fetchCompanyMasterData } from "../../services/companyMasterDataService";
import masterDataConfigService from "../../services/masterDataConfigService";

import PendingStockOperationNotice from "../../components/stock/PendingStockOperationNotice";
import StockPostingContextCard from "../../components/stock/StockPostingContextCard";
import StockMovementAuditLoader from "../../components/stock/StockMovementAuditLoader";
import { pendingStockOperation, STOCK_POSTING_JOURNAL_EVENT } from "../../utils/idempotentStockPosting";
import { stockPostingErrorMessage } from "../../utils/stockPostingPresentation";
import "./SalesDocumentEditPage.css";

const today = () => new Intl.DateTimeFormat("sv-SE", { timeZone: process.env.REACT_APP_BUSINESS_TIME_ZONE || "Europe/Warsaw" }).format(new Date());

const DEFAULT_HEADER = {
  document_type: "invoice",
  sale_date: today(),
  stock_business_date: today(),
  issue_date: today(),
  due_date: "",
  place_of_issue: "",
  payment_term_days: "0",
  bank_account_id: "",
  buyer_type: "company",
  buyer_name: "",
  buyer_tax_id: "",
  buyer_email: "",
  buyer_phone: "",
  buyer_address_line1: "",
  buyer_address_line2: "",
  buyer_postal_code: "",
  buyer_city: "",
  buyer_country_code: "PL",
  currency: "PLN",
  payment_method: "transfer",
  payment_status: "unpaid",
  cash_discount_percent: "0",
  cash_discount_days: "0",
  cash_discount_due_date: "",
  cash_discount_note: "",
  stock_location_id: "",
  internal_note: "",
  print_note: "",
  comment: "",
};

function n(value) {
  const parsed = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : 0;
}

function roundMoney(value) {
  return Math.round((n(value) + Number.EPSILON) * 100) / 100;
}

function formatMoney(value, currency = "PLN") {
  return `${roundMoney(value).toFixed(2)} ${currency || "PLN"}`;
}

function addDays(dateOnly, days) {
  if (!dateOnly || !Number.isFinite(Number(days))) return "";
  const d = new Date(`${dateOnly}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + Number(days || 0));
  return d.toISOString().slice(0, 10);
}

function clampPercent(value) {
  const percent = n(value || 0);
  if (percent < 0) return 0;
  if (percent > 100) return 100;
  return percent;
}

function calculateLinePreview(line) {
  const quantity = n(line.quantity || 0);
  const grossUnit = n(line.unit_price_gross || 0);
  const vatRate = n(line.vat_rate_percent || 0);
  const discountPercent = clampPercent(line.discount_percent || 0);
  const discountAmountGross = Math.max(0, n(line.discount_amount_gross || 0));

  const grossBeforeDiscount = roundMoney(quantity * grossUnit);
  const percentDiscountGross = roundMoney(grossBeforeDiscount * (discountPercent / 100));
  const discountGross = roundMoney(Math.min(grossBeforeDiscount, percentDiscountGross + discountAmountGross));
  const gross = roundMoney(Math.max(0, grossBeforeDiscount - discountGross));
  const net = vatRate <= -100 ? gross : roundMoney(gross / (1 + vatRate / 100));
  const vat = roundMoney(gross - net);

  return { net, vat, gross, grossBeforeDiscount, discountGross };
}

function calculateCashDiscountPreview(grossTotal, cashDiscountPercent) {
  return roundMoney(grossTotal * (clampPercent(cashDiscountPercent || 0) / 100));
}

function normalizeLoadedLine(line) {
  return {
    local_id: `${line.id || "loaded"}-${line.line_no || Date.now()}`,
    id: line.id,
    line_type: line.line_type || "manual",
    product_id: line.product_id || "",
    service_id: line.service_id || "",
    name: line.name || "",
    description: line.description || "",
    quantity: line.quantity ?? "1",
    unit_code: line.unit_code || "pcs",
    unit_price_gross: line.unit_price_gross ?? "0",
    vat_rate_percent: line.vat_rate_percent ?? "0",
    discount_percent: line.discount_percent ?? "0",
    discount_amount_gross: line.discount_amount_gross ?? "0",
    stock_unit_code: line.stock_unit_code || "",
    stock_quantity: line.stock_quantity ?? "",
    stock_movement_id: line.stock_movement_id || null,
    stock_posting_result_json: line.stock_posting_result_json || null,
    stock_location_id: line.stock_location_id || "",
    available_quantity: line.available_quantity ?? "",
    available_locations: Array.isArray(line.available_locations) ? line.available_locations : [],
  };
}

function sortRows(a, b) {
  return Number(a.sort_order || 0) - Number(b.sort_order || 0) || Number(a.id || 0) - Number(b.id || 0);
}

function asString(value) {
  return String(value ?? "").trim();
}

function localPaymentMethodName(method, language) {
  const isPolish = String(language || "").toLowerCase().startsWith("pl");
  return asString(isPolish ? method?.name_pl : method?.name_en) ||
    asString(method?.name_en) ||
    asString(method?.name_pl) ||
    asString(method?.code);
}

function salesDocumentErrorMessage(err, t) {
  // Service-layer errors intentionally preserve backend error codes. The editor
  // translates known business errors locally so operators see clear Polish/EN
  // messages instead of technical strings such as
  // "postSalesDocument: Insufficient stock in selected location".
  const code = asString(err?.code || err?.data?.code).toUpperCase();
  const message = asString(err?.message);

  if (code === "INSUFFICIENT_STOCK" || message.includes("Insufficient stock")) {
    return t("sales:messages.insufficientStockSelectedLocation");
  }

  return stockPostingErrorMessage(err, t);
}

function stockBalanceKey(productId, locationId) {
  return `${asString(productId)}:${asString(locationId)}`;
}

function stockUnitForLine(line) {
  return line?.stock_unit_code || line?.unit_code || "pcs";
}

function formatQuantity(value, unit = "pcs") {
  if (value === null || value === undefined || value === "") return "—";
  const parsed = Number(value);
  const text = Number.isFinite(parsed)
    ? parsed.toFixed(3).replace(/\.?0+$/, "")
    : String(value);
  return `${text} ${unit || "pcs"}`;
}

function paymentMethodCodeFromSettings(settings = {}, methods = []) {
  const selectedId = asString(settings.default_payment_method_id);
  if (!selectedId) return "";
  const selected = methods.find((method) => asString(method?.id) === selectedId);
  return asString(selected?.code);
}

function defaultPaymentTermFromSettings(settings = {}, methods = []) {
  // Document Settings is the first source for invoice payment term defaults.
  // If it is empty, a Payment Method can suggest its own default term. Company
  // Master Data is intentionally not used for payment terms anymore.
  const configuredTerm = settings.default_payment_term_days;
  if (configuredTerm !== undefined && configuredTerm !== null && asString(configuredTerm) !== "") {
    return String(configuredTerm);
  }

  const selectedId = asString(settings.default_payment_method_id);
  const selected = methods.find((method) => asString(method?.id) === selectedId);
  const methodTerm = selected?.default_payment_term_days;
  if (methodTerm !== undefined && methodTerm !== null && asString(methodTerm) !== "") {
    return String(methodTerm);
  }

  return "0";
}

function defaultHeaderFromDocumentSettings(settings = {}, methods = [], previousHeader = DEFAULT_HEADER) {
  const paymentTermDays = defaultPaymentTermFromSettings(settings, methods);
  const issueDate = previousHeader.issue_date || today();
  const cashDiscountDays = previousHeader.cash_discount_days || "0";

  return {
    place_of_issue: asString(settings.default_place_of_issue),
    currency: asString(settings.default_currency || "PLN").toUpperCase().slice(0, 3) || "PLN",
    payment_term_days: paymentTermDays,
    due_date: addDays(issueDate, Number(paymentTermDays || 0)),
    payment_method: paymentMethodCodeFromSettings(settings, methods) || previousHeader.payment_method || "transfer",
    bank_account_id: settings.default_bank_account_id ? String(settings.default_bank_account_id) : "",
    cash_discount_due_date: cashDiscountDays ? addDays(issueDate, Number(cashDiscountDays || 0)) : previousHeader.cash_discount_due_date,
  };
}

function buildSellerPreview(companyData = {}) {
  const company = companyData.company || {};
  const addresses = [...(companyData.addresses || [])].sort(sortRows);
  const bankAccounts = [...(companyData.bankAccounts || [])].sort(sortRows);

  const address =
    addresses.find((row) => row.address_type === "invoicing" && row.is_default) ||
    addresses.find((row) => row.address_type === "invoicing") ||
    addresses.find((row) => row.address_type === "registered" && row.is_default) ||
    addresses.find((row) => row.address_type === "registered") ||
    addresses.find((row) => row.is_default) ||
    addresses[0] ||
    null;

  const bankAccount = bankAccounts.find((row) => row.is_default) || bankAccounts[0] || null;

  return {
    seller_legal_name: company.legal_name || "",
    seller_trade_name: company.trade_name || "",
    seller_tax_id: company.tax_id || "",
    seller_regon: company.regon || "",
    seller_krs: company.krs || "",
    seller_public_email: company.public_email || "",
    seller_invoice_email: company.invoice_email || "",
    seller_phone: company.phone || "",
    seller_website: company.website || "",
    seller_address_line1: address?.address_line1 || "",
    seller_address_line2: address?.address_line2 || "",
    seller_postal_code: address?.postal_code || "",
    seller_city: address?.city || "",
    seller_address_country_code: address?.country_code || company.country_code || "PL",
    seller_bank_account_id: bankAccount?.id || "",
    seller_bank_label: bankAccount?.account_label || "",
    seller_bank_name: bankAccount?.bank_name || "",
    seller_iban: bankAccount?.iban || "",
    seller_bank_account_number: bankAccount?.account_number || "",
    seller_swift_bic: bankAccount?.swift_bic || "",
    seller_bank_currency: bankAccount?.currency || company.default_currency || "PLN",
    };
}

function buildVatSummary(lines) {
  const buckets = new Map();

  for (const line of lines) {
    const rate = Number(n(line.vat_rate_percent || 0).toFixed(2));
    const key = rate.toFixed(2);
    const preview = calculateLinePreview(line);
    const current = buckets.get(key) || { vat_rate_percent: rate, net: 0, vat: 0, gross: 0, discountGross: 0 };
    current.net = roundMoney(current.net + preview.net);
    current.vat = roundMoney(current.vat + preview.vat);
    current.gross = roundMoney(current.gross + preview.gross);
    current.discountGross = roundMoney(current.discountGross + preview.discountGross);
    buckets.set(key, current);
  }

  return [...buckets.values()].sort((a, b) => a.vat_rate_percent - b.vat_rate_percent);
}

function preparePayload(header, lines) {
  return {
    ...header,
    due_date: header.due_date || null,
    place_of_issue: header.place_of_issue || null,
    payment_term_days: header.payment_term_days === "" ? 0 : header.payment_term_days,
    cash_discount_percent: header.cash_discount_percent === "" ? 0 : String(header.cash_discount_percent).replace(",", "."),
    cash_discount_days: header.cash_discount_days === "" ? 0 : header.cash_discount_days,
    cash_discount_due_date: header.cash_discount_due_date || null,
    cash_discount_note: header.cash_discount_note || null,
    bank_account_id: header.bank_account_id || null,
    stock_location_id: header.stock_location_id || null,
    buyer_name: header.buyer_name || null,
    buyer_tax_id: header.buyer_tax_id || null,
    buyer_email: header.buyer_email || null,
    buyer_phone: header.buyer_phone || null,
    buyer_address_line1: header.buyer_address_line1 || null,
    buyer_address_line2: header.buyer_address_line2 || null,
    buyer_postal_code: header.buyer_postal_code || null,
    buyer_city: header.buyer_city || null,
    internal_note: header.internal_note || null,
    print_note: header.print_note || null,
    comment: header.comment || null,
    lines: lines.map((line) => ({
      line_type: line.line_type,
      product_id: line.product_id || null,
      service_id: line.service_id || null,
      name: line.name,
      description: line.description || null,
      quantity: String(line.quantity || "1").replace(",", "."),
      unit_code: line.unit_code || "pcs",
      unit_price_gross: String(line.unit_price_gross || "0").replace(",", "."),
      vat_rate_percent: String(line.vat_rate_percent || "0").replace(",", "."),
      discount_percent: String(line.discount_percent || "0").replace(",", "."),
      discount_amount_gross: String(line.discount_amount_gross || "0").replace(",", "."),
      stock_location_id: line.stock_location_id || header.stock_location_id || null,
    })),
  };
}

const DetailRow = ({ label, value }) => (
  <div className="sales-detail-row">
    <span>{label}</span>
    <strong>{value || "—"}</strong>
  </div>
);

const SalesDocumentEditPage = () => {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(["common", "sales", "stock"]);

  const [header, setHeader] = useState(DEFAULT_HEADER);
  const [lines, setLines] = useState([]);
  const [documentStatus, setDocumentStatus] = useState("draft");
  const [documentNumber, setDocumentNumber] = useState("");
  const [locations, setLocations] = useState([]);
  const [lineStockBalances, setLineStockBalances] = useState({});
  const [companyData, setCompanyData] = useState({ company: {}, addresses: [], bankAccounts: [] });
  const [documentSettings, setDocumentSettings] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [postedSnapshots, setPostedSnapshots] = useState({ seller: null, buyer: null, payment: null, tax: null });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const [auditMovementId, setAuditMovementId] = useState(null);
  const [pendingPost, setPendingPost] = useState(() => pendingStockOperation("sales"));
  const documentRequest = useRef(0);
  useEffect(() => { const update = () => setPendingPost(pendingStockOperation("sales"));
    window.addEventListener(STOCK_POSTING_JOURNAL_EVENT, update); return () => window.removeEventListener(STOCK_POSTING_JOURNAL_EVENT, update); }, []);
  const isReadOnly = documentStatus !== "draft" || Boolean(pendingPost);
  const sellerPreview = useMemo(() => buildSellerPreview(companyData), [companyData]);
  const activeSeller = postedSnapshots.seller || sellerPreview;
  const bankAccounts = companyData.bankAccounts || [];

  const totals = useMemo(() => lines.reduce(
    (acc, line) => {
      const preview = calculateLinePreview(line);
      acc.net += preview.net;
      acc.vat += preview.vat;
      acc.gross += preview.gross;
      acc.grossBeforeDiscount += preview.grossBeforeDiscount;
      acc.discountGross += preview.discountGross;
      return acc;
    },
    { net: 0, vat: 0, gross: 0, grossBeforeDiscount: 0, discountGross: 0 }
  ), [lines]);

  const vatSummary = useMemo(() => buildVatSummary(lines), [lines]);
  const cashDiscountAmount = useMemo(
    () => calculateCashDiscountPreview(totals.gross, header.cash_discount_percent),
    [totals.gross, header.cash_discount_percent]
  );
  const cashDiscountPayableAmount = roundMoney(totals.gross - cashDiscountAmount);

  const selectedBankAccount = useMemo(() => {
    const selectedId = String(header.bank_account_id || "");
    return bankAccounts.find((row) => String(row.id) === selectedId) || bankAccounts.find((row) => row.is_default) || bankAccounts[0] || null;
  }, [bankAccounts, header.bank_account_id]);

  const activePaymentMethods = useMemo(() => {
    return [...paymentMethods]
      .filter((method) => method?.is_active !== false)
      .sort((a, b) => Number(a?.sort_order || 0) - Number(b?.sort_order || 0) || asString(a?.code).localeCompare(asString(b?.code)));
  }, [paymentMethods]);

  const selectedPaymentMethod = useMemo(() => {
    return activePaymentMethods.find((method) => asString(method?.code) === asString(header.payment_method)) || null;
  }, [activePaymentMethods, header.payment_method]);

  const loadDocumentDefaults = useCallback(async () => {
    if (!isNew) return;

    // New invoice drafts must take their operational defaults from Document
    // Settings and Payment Methods. This mirrors the backend source-of-truth
    // split introduced during the invoice cleanup:
    // - Company Master Data = seller identity,
    // - Document Settings = document issuing defaults,
    // - Payment Methods = payment behavior and stable payment codes.
    const [settings, methods] = await Promise.all([
      masterDataConfigService.getDocumentSettings(),
      masterDataConfigService.listPaymentMethods({ includeInactive: false }),
    ]);

    const normalizedSettings = settings || {};
    const normalizedMethods = Array.isArray(methods) ? methods : [];

    setDocumentSettings(normalizedSettings);
    setPaymentMethods(normalizedMethods);

    setHeader((prev) => {
      const defaults = defaultHeaderFromDocumentSettings(normalizedSettings, normalizedMethods, prev);
      return {
        ...prev,
        place_of_issue: prev.place_of_issue || defaults.place_of_issue,
        currency: prev.currency || defaults.currency,
        payment_term_days: prev.payment_term_days && prev.payment_term_days !== "0" ? prev.payment_term_days : defaults.payment_term_days,
        due_date: prev.due_date || defaults.due_date,
        payment_method: prev.payment_method && prev.payment_method !== DEFAULT_HEADER.payment_method ? prev.payment_method : defaults.payment_method,
        bank_account_id: prev.bank_account_id || defaults.bank_account_id,
        cash_discount_due_date: prev.cash_discount_due_date || defaults.cash_discount_due_date,
      };
    });
  }, [isNew]);

  const loadCompany = useCallback(async () => {
    const data = await fetchCompanyMasterData();
    setCompanyData(data);

    // Company Master Data is now used only for seller identity, addresses,
    // contact data, bank accounts and branding. It is not a source for document
    // rules such as place of issue, payment method or payment term anymore.
    // A default bank account may still be used as a safe fallback if Document
    // Settings does not explicitly select one.
    if (isNew) {
      const preview = buildSellerPreview(data);
      setHeader((prev) => ({
        ...prev,
        bank_account_id: prev.bank_account_id || (preview.seller_bank_account_id ? String(preview.seller_bank_account_id) : ""),
      }));
    }
  }, [isNew]);

  const loadLocations = useCallback(async () => {
    const rows = await fetchStockLocations();
    setLocations(rows);

    if (!header.stock_location_id) {
      const reception = rows.find((row) => String(row.code || "").toUpperCase() === "RECEPTION");
      const fallback = reception || rows[0];
      if (fallback) setHeader((prev) => ({ ...prev, stock_location_id: String(fallback.id) }));
    }
  }, [header.stock_location_id]);

  const loadLineStockBalances = useCallback(async () => {
    // Product lines should show the operator the available quantity in the
    // selected stock location before posting. This is a frontend diagnostic only:
    // the backend still validates stock authoritatively during posting.
    const productIds = [...new Set(lines.filter((line) => line.line_type === "product" && line.product_id).map((line) => String(line.product_id)))];

    if (productIds.length === 0) {
      setLineStockBalances({});
      return;
    }

    try {
      const responses = await Promise.all(productIds.map((productId) => fetchStockBalances({ product_id: productId })));
      const next = {};

      responses.flat().forEach((row) => {
        next[stockBalanceKey(row.product_id, row.location_id)] = row;
      });

      setLineStockBalances(next);
    } catch (err) {
      // Stock preview loading must never block invoice drafting. A failed stock
      // preview only hides the informational quantity column; posting still runs
      // the real backend stock validation and will return a translated error if
      // there is not enough stock.
      setLineStockBalances({});
    }
  }, [lines]);

  function availableQuantityForLine(line) {
    const locationId = line.stock_location_id || header.stock_location_id || "";
    const balance = lineStockBalances[stockBalanceKey(line.product_id, locationId)];

    if (balance) return balance.quantity ?? 0;

    const locationBalance = Array.isArray(line.available_locations)
      ? line.available_locations.find((row) => asString(row.location_id) === asString(locationId))
      : null;

    if (locationBalance) return locationBalance.quantity ?? 0;

    if (line.available_quantity !== "" && line.available_quantity !== null && line.available_quantity !== undefined) {
      return line.available_quantity;
    }

    return 0;
  }

  function selectedStockLocationNameForLine(line) {
    // The line-level stock location is the operative location for the product
    // issue. If it is empty, the document-level stock location acts as the
    // default. The helper is intentionally UI-only; the backend still validates
    // the final stock location during posting.
    const locationId = line.stock_location_id || header.stock_location_id || "";
    const selected = locations.find((loc) => asString(loc.id) === asString(locationId));
    return selected?.name || t("sales:editor.defaultStockLocation");
  }

  function stockAvailabilityForLine(line) {
    // Current stock is a dynamic operational value, not part of the historical
    // invoice content. Therefore it is shown only for draft product lines as a
    // small contextual hint near the location selector. Posted documents should
    // show the location used for the stock issue, but not current stock levels.
    const available = n(availableQuantityForLine(line));
    const requested = n(line.quantity || line.stock_quantity || 0);
    const remaining = available - requested;
    const insufficient = requested > available;

    return {
      available,
      requested,
      remaining,
      insufficient,
      unit: stockUnitForLine(line),
      locationName: selectedStockLocationNameForLine(line),
    };
  }

  const applyDocument = useCallback((doc) => {
      setDocumentStatus(doc?.status || "draft");
      setDocumentNumber(doc?.document_number || "");
      setPostedSnapshots({
        seller: doc?.seller_snapshot_json || null,
        buyer: doc?.buyer_snapshot_json || null,
        payment: doc?.payment_snapshot_json || null,
        tax: doc?.tax_summary_json || null,
      });
      setHeader({
        ...DEFAULT_HEADER,
        document_type: doc?.document_type || DEFAULT_HEADER.document_type,
        stock_business_date: doc?.stock_business_date || (doc?.status === "draft" ? doc?.sale_date || "" : ""),
        sale_date: String(doc?.sale_date || DEFAULT_HEADER.sale_date).slice(0, 10),
        issue_date: String(doc?.issue_date || DEFAULT_HEADER.issue_date).slice(0, 10),
        due_date: doc?.due_date ? String(doc.due_date).slice(0, 10) : "",
        place_of_issue: doc?.place_of_issue || "",
        payment_term_days: String(doc?.payment_term_days ?? 0),
        bank_account_id: doc?.bank_account_id ? String(doc.bank_account_id) : "",
        buyer_type: doc?.buyer_type || DEFAULT_HEADER.buyer_type,
        buyer_name: doc?.buyer_name || "",
        buyer_tax_id: doc?.buyer_tax_id || "",
        buyer_email: doc?.buyer_email || "",
        buyer_phone: doc?.buyer_phone || "",
        buyer_address_line1: doc?.buyer_address_line1 || "",
        buyer_address_line2: doc?.buyer_address_line2 || "",
        buyer_postal_code: doc?.buyer_postal_code || "",
        buyer_city: doc?.buyer_city || "",
        buyer_country_code: doc?.buyer_country_code || "PL",
        currency: doc?.currency || "PLN",
        payment_method: doc?.payment_method || "transfer",
        payment_status: doc?.payment_status || "unpaid",
        cash_discount_percent: String(doc?.cash_discount_percent ?? 0),
        cash_discount_days: String(doc?.cash_discount_days ?? 0),
        cash_discount_due_date: doc?.cash_discount_due_date ? String(doc.cash_discount_due_date).slice(0, 10) : "",
        cash_discount_note: doc?.cash_discount_note || "",
        stock_location_id: doc?.stock_location_id || "",
        internal_note: doc?.internal_note || "",
        print_note: doc?.print_note || "",
        comment: doc?.comment || "",
      });
      setLines(Array.isArray(doc?.lines) ? doc.lines.map(normalizeLoadedLine) : []);
  }, []);

  const loadDocument = useCallback(async () => {
    if (isNew) return;
    const requestId = ++documentRequest.current;

    setLoading(true);
    setError("");

    try {
      const doc = await fetchSalesDocumentById(id);
      if (requestId === documentRequest.current) applyDocument(doc);
    } catch (err) {
      if (requestId === documentRequest.current) setError(salesDocumentErrorMessage(err, t) || t("sales:messages.loadDocumentFailed"));
    } finally {
      setLoading(false);
    }
  }, [id, isNew, t, applyDocument]);

  useEffect(() => {
    loadDocumentDefaults().catch((err) => setError(salesDocumentErrorMessage(err, t) || t("sales:messages.loadDocumentSettingsFailed")));
  }, [loadDocumentDefaults, t]);

  useEffect(() => {
    loadCompany().catch((err) => setError(salesDocumentErrorMessage(err, t) || t("sales:messages.loadCompanyMasterDataFailed")));
  }, [loadCompany, t]);

  useEffect(() => {
    loadLocations().catch((err) => setError(salesDocumentErrorMessage(err, t) || t("sales:messages.loadLocationsFailed")));
  }, [loadLocations, t]);

  useEffect(() => {
    loadLineStockBalances();
  }, [loadLineStockBalances]);

  useEffect(() => {
    loadDocument();
  }, [loadDocument]);

  function updateHeader(patch) {
    setHeader((prev) => ({ ...prev, ...patch }));
  }

  function setIssueDate(issueDate) {
    setHeader((prev) => ({
      ...prev,
      issue_date: issueDate,
      due_date: prev.payment_term_days ? addDays(issueDate, Number(prev.payment_term_days || 0)) : prev.due_date,
      cash_discount_due_date: prev.cash_discount_days ? addDays(issueDate, Number(prev.cash_discount_days || 0)) : prev.cash_discount_due_date,
    }));
  }

  function setPaymentTermDays(value) {
    setHeader((prev) => ({
      ...prev,
      payment_term_days: value,
      due_date: addDays(prev.issue_date, Number(value || 0)),
    }));
  }

  function setPaymentMethodCode(value) {
    const method = activePaymentMethods.find((row) => asString(row?.code) === asString(value));
    setHeader((prev) => {
      const patch = { payment_method: value };

      // When the user selects a payment method on a fresh/empty payment term,
      // use the method's suggested term. If the user already changed the term,
      // keep that explicit document value. Document Settings has already seeded
      // the draft before this point.
      if ((prev.payment_term_days === "" || prev.payment_term_days === null || prev.payment_term_days === undefined) && method?.default_payment_term_days !== undefined) {
        patch.payment_term_days = String(method.default_payment_term_days || 0);
        patch.due_date = addDays(prev.issue_date, Number(method.default_payment_term_days || 0));
      }

      return { ...prev, ...patch };
    });
  }

  function setCashDiscountDays(value) {
    setHeader((prev) => ({
      ...prev,
      cash_discount_days: value,
      cash_discount_due_date: addDays(prev.issue_date, Number(value || 0)),
    }));
  }

  function addProductLine(product) {
    const stockUnit = product.stock_unit_code || "pcs";
    setLines((prev) => [
      ...prev,
      {
        local_id: `product-${product.id}-${Date.now()}`,
        line_type: "product",
        product_id: product.id,
        service_id: "",
        name: product.name_public,
        description: product.sku ? `SKU: ${product.sku}` : "",
        quantity: "1",
        unit_code: stockUnit,
        unit_price_gross: product.price_gross ?? "0",
        vat_rate_percent: product.vat_rate_percent ?? "0",
        discount_percent: "0",
        discount_amount_gross: "0",
        stock_unit_code: stockUnit,
        stock_quantity: "1",
        stock_location_id: header.stock_location_id || "",
        available_quantity: product.available_quantity ?? product.global_stock_quantity ?? "",
        available_locations: Array.isArray(product.available_locations) ? product.available_locations : [],
      },
    ]);
  }

  function addServiceLine(service) {
    setLines((prev) => [
      ...prev,
      {
        local_id: `service-${service.id}-${Date.now()}`,
        line_type: "service",
        product_id: "",
        service_id: service.id,
        name: service.name_public,
        description: service.duration_minutes ? `${service.duration_minutes} min` : "",
        quantity: "1",
        unit_code: "service",
        unit_price_gross: service.price_gross ?? "0",
        vat_rate_percent: service.vat_rate_percent ?? "0",
        discount_percent: "0",
        discount_amount_gross: "0",
        stock_unit_code: "",
        stock_quantity: "",
        stock_location_id: "",
      },
    ]);
  }

  function addManualLine() {
    setLines((prev) => [
      ...prev,
      {
        local_id: `manual-${Date.now()}`,
        line_type: "manual",
        product_id: "",
        service_id: "",
        name: t("sales:lineTypes.manual"),
        description: "",
        quantity: "1",
        unit_code: "pcs",
        unit_price_gross: "0",
        vat_rate_percent: "23",
        discount_percent: "0",
        discount_amount_gross: "0",
        stock_unit_code: "",
        stock_quantity: "",
        stock_location_id: "",
      },
    ]);
  }

  function updateLine(index, patch) {
    setLines((prev) => prev.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  }

  function removeLine(index) {
    setLines((prev) => prev.filter((_, i) => i !== index));
  }

  async function saveDraft() {
    if (pendingStockOperation("sales")) return;
    setSaving(true);
    setError("");
    setInfo("");

    try {
      const payload = preparePayload(header, lines);
      const saved = isNew ? await createSalesDocument(payload) : await updateSalesDocument(id, payload);
      setInfo(t("sales:messages.draftSaved"));

      if (isNew && saved?.id) {
        navigate(`/sales/${saved.id}`, { replace: true });
      } else {
        await loadDocument();
      }
    } catch (err) {
      setError(salesDocumentErrorMessage(err, t) || t("sales:messages.saveDraftFailed"));
    } finally {
      setSaving(false);
    }
  }

  async function saveAndPost() {
    if (pendingStockOperation("sales")) return;
    setPosting(true); setError(""); setInfo("");
    try {
      const payload = preparePayload(header, lines);
      const saved = isNew ? await createSalesDocument(payload) : await updateSalesDocument(id, payload);
      // Keep the stable saved document ID before sending the committing request.
      // A lost response must never create a replacement draft on the next click.
      applyDocument(saved);
      navigate(`/sales/${saved.id || id}`, { replace: true });
      const posted = await postSalesDocument(saved.id || id);
      documentRequest.current += 1;
      applyDocument(posted);
      setInfo(t("sales:messages.postedFromEditor"));
    } catch (err) {
      setError(salesDocumentErrorMessage(err, t) || t("sales:messages.postDocumentFailed"));
    } finally { setPosting(false); }
  }


  return (
    <div className="sales-page sales-invoice-editor">
      <PendingStockOperationNotice scope="sales" onResolved={(doc) => { documentRequest.current += 1; applyDocument(doc); navigate(`/sales/${doc.id}`, { replace: true }); }} />
      <StockMovementAuditLoader movementId={auditMovementId} onClose={() => setAuditMovementId(null)} />
      <div className="sales-page-header">
        <div>
          <p className="sales-eyebrow">{t("sales:editor.eyebrow")}</p>
          <h1>{isNew ? t("sales:editor.newInvoiceTitle") : documentNumber || t("sales:editor.draftNumber", { id })}</h1>
          <p className="sales-muted">{t("sales:editor.invoiceSubtitle")}</p>
        </div>
        <div className="sales-header-actions">
          <Link to="/sales" className="btn btn-outline-secondary">{t("sales:editor.backToList")}</Link>
          {!isReadOnly && (
            <button type="button" className="btn btn-outline-primary" onClick={saveDraft} disabled={saving || posting || Boolean(pendingPost)}>
              {t("sales:editor.saveDraft")}
            </button>
          )}
          {!isReadOnly && (
            <button type="button" className="btn btn-success" onClick={saveAndPost} disabled={saving || posting || Boolean(pendingPost) || lines.length === 0}>
              {t("sales:editor.issueInvoice")}
            </button>
          )}
        </div>
      </div>

      {loading && <div className="alert alert-info">{t("sales:editor.loading")}</div>}
      {error && <div className="alert alert-danger">{error}</div>}
      {info && <div className="alert alert-success">{info}</div>}
      {isReadOnly && (
        <div className="alert alert-secondary">
          {t("sales:editor.snapshotReadOnlyNotice", { status: t(`sales:statuses.${documentStatus}`, { defaultValue: documentStatus }) })}
        </div>
      )}

      <section className="sales-panel sales-document-status-panel">
        <DetailRow label={t("sales:editor.fields.status")} value={t(`sales:statuses.${documentStatus}`, { defaultValue: documentStatus })} />
        <DetailRow label={t("sales:editor.fields.documentNumber")} value={documentNumber || t("sales:editor.notAssignedYet")} />
        <DetailRow label={t("sales:editor.fields.snapshotStatus")} value={postedSnapshots.seller ? t("sales:editor.snapshotFrozen") : t("sales:editor.snapshotWillBeCreated")} />
      </section>

      <section className="sales-panel">
        <h2>{t("sales:editor.sections.documentDetails")}</h2>
        <div className="sales-form-grid sales-form-grid--four">
          <label>{t("sales:editor.fields.documentType")}
            <select value={header.document_type} disabled={isReadOnly} onChange={(e) => updateHeader({ document_type: e.target.value })}>
              <option value="invoice">{t("sales:documentTypes.invoice")}</option>
              <option value="receipt">{t("sales:documentTypes.receipt")}</option>
              <option value="internal_sale">{t("sales:documentTypes.internal_sale")}</option>
            </select>
          </label>
          <label>{t("sales:editor.fields.issueDate")}
            <input type="date" value={header.issue_date} disabled={isReadOnly} onChange={(e) => setIssueDate(e.target.value)} />
          </label>
          <label>{t("sales:editor.fields.saleDate")}
            <input type="date" value={header.sale_date} disabled={isReadOnly} onChange={(e) => updateHeader({ sale_date: e.target.value, stock_business_date: header.stock_business_date === header.sale_date ? e.target.value : header.stock_business_date })} />
          </label>
          <label>{t("stock:integration.stockBusinessDate")}
            <input type="date" value={header.stock_business_date || ""} disabled={isReadOnly} onChange={(e) => updateHeader({ stock_business_date: e.target.value })} />
            <small>{t("stock:integration.salesDateHint")}</small>
          </label>
          <label>{t("sales:editor.fields.placeOfIssue")}
            <input value={header.place_of_issue} disabled={isReadOnly} onChange={(e) => updateHeader({ place_of_issue: e.target.value })} placeholder={documentSettings?.default_place_of_issue || ""} />
          </label>
        </div>
      </section>

      <p className="stock-read-context">{t("stock:integration.currentAvailabilityHint")}</p>
      {lines.filter(line => line.stock_movement_id).map(line => <StockPostingContextCard key={line.id || line.local_id}
        title={line.name} snapshot={line.stock_posting_result_json} movementId={line.stock_movement_id} onOpenMovement={setAuditMovementId} />)}
      <div className="sales-grid sales-grid--seller-buyer">
        <section className="sales-panel">
          <h2>{t("sales:editor.sections.seller")}</h2>
          <p className="sales-muted">{isReadOnly ? t("sales:editor.sellerFrozenHint") : t("sales:editor.sellerDraftHint")}</p>
          <div className="sales-snapshot-card">
            <DetailRow label={t("sales:editor.fields.companyName")} value={activeSeller.seller_legal_name || activeSeller.seller_trade_name} />
            <DetailRow label={t("sales:editor.fields.taxId")} value={activeSeller.seller_tax_id} />
            <DetailRow label={t("sales:editor.fields.address")} value={[activeSeller.seller_address_line1, activeSeller.seller_postal_code, activeSeller.seller_city].filter(Boolean).join(", ")} />
            <DetailRow label={t("sales:editor.fields.email")} value={activeSeller.seller_invoice_email || activeSeller.seller_public_email} />
            <DetailRow label={t("sales:editor.fields.phone")} value={activeSeller.seller_phone} />
          </div>
          {!isReadOnly && (
            <Link to="/master-data/company" className="btn btn-sm btn-outline-secondary mt-2">
              {t("sales:editor.openCompanyMasterData")}
            </Link>
          )}
        </section>

        <section className="sales-panel">
          <h2>{t("sales:editor.sections.buyerSnapshot")}</h2>
          <p className="sales-muted">{t("sales:editor.buyerSnapshotHint")}</p>
          <div className="sales-form-grid">
            <label>{t("sales:editor.fields.buyerType")}
              <select value={header.buyer_type} disabled={isReadOnly} onChange={(e) => updateHeader({ buyer_type: e.target.value })}>
                <option value="anonymous">{t("sales:buyerTypes.anonymous")}</option>
                <option value="private_person">{t("sales:buyerTypes.private_person")}</option>
                <option value="company">{t("sales:buyerTypes.company")}</option>
              </select>
            </label>
            <label>{t("sales:editor.fields.buyerName")}<input value={header.buyer_name} placeholder={t("sales:editor.placeholders.buyerNameAnonymous")} disabled={isReadOnly} onChange={(e) => updateHeader({ buyer_name: e.target.value })} /></label>
            <label>{t("sales:editor.fields.taxId")}<input value={header.buyer_tax_id} disabled={isReadOnly} onChange={(e) => updateHeader({ buyer_tax_id: e.target.value })} /></label>
            <label>{t("sales:editor.fields.email")}<input value={header.buyer_email} disabled={isReadOnly} onChange={(e) => updateHeader({ buyer_email: e.target.value })} /></label>
            <label>{t("sales:editor.fields.address")}<input value={header.buyer_address_line1} disabled={isReadOnly} onChange={(e) => updateHeader({ buyer_address_line1: e.target.value })} /></label>
            <label>{t("sales:editor.fields.city")}<input value={header.buyer_city} disabled={isReadOnly} onChange={(e) => updateHeader({ buyer_city: e.target.value })} /></label>
          </div>
        </section>
      </div>

      <section className="sales-panel">
        <h2>{t("sales:editor.sections.paymentAndBank")}</h2>
        <div className="sales-form-grid sales-form-grid--four">
          <label>{t("sales:editor.fields.paymentMethod")}
            <select value={header.payment_method} disabled={isReadOnly} onChange={(e) => setPaymentMethodCode(e.target.value)}>
              {activePaymentMethods.length > 0 ? activePaymentMethods.map((method) => (
                <option key={method.id || method.code} value={method.code}>
                  {localPaymentMethodName(method, i18n.language)}
                </option>
              )) : (
                <>
                  <option value="cash">{t("sales:paymentMethods.cash")}</option>
                  <option value="card">{t("sales:paymentMethods.card")}</option>
                  <option value="transfer">{t("sales:paymentMethods.transfer")}</option>
                  <option value="other">{t("sales:paymentMethods.other")}</option>
                </>
              )}
            </select>
            {selectedPaymentMethod?.default_payment_term_days !== undefined && selectedPaymentMethod?.default_payment_term_days !== null ? (
              <small>{t("sales:editor.paymentMethodDefaultTermHint", { days: selectedPaymentMethod.default_payment_term_days })}</small>
            ) : null}
          </label>
          <label>{t("sales:editor.fields.paymentTermDays")}
            <input type="number" min="0" value={header.payment_term_days} disabled={isReadOnly} onChange={(e) => setPaymentTermDays(e.target.value)} />
          </label>
          <label>{t("sales:editor.fields.dueDate")}
            <input type="date" value={header.due_date} disabled={isReadOnly} onChange={(e) => updateHeader({ due_date: e.target.value })} />
          </label>
          <label>{t("sales:editor.fields.currency")}
            <input value={header.currency} disabled={isReadOnly} onChange={(e) => updateHeader({ currency: e.target.value.toUpperCase().slice(0, 3) })} />
          </label>
          <label>{t("sales:editor.fields.cashDiscountPercent")}
            <input type="number" min="0" max="100" step="0.01" value={header.cash_discount_percent} disabled={isReadOnly} onChange={(e) => updateHeader({ cash_discount_percent: e.target.value })} />
          </label>
          <label>{t("sales:editor.fields.cashDiscountDays")}
            <input type="number" min="0" value={header.cash_discount_days} disabled={isReadOnly} onChange={(e) => setCashDiscountDays(e.target.value)} />
          </label>
          <label>{t("sales:editor.fields.cashDiscountDueDate")}
            <input type="date" value={header.cash_discount_due_date} disabled={isReadOnly} onChange={(e) => updateHeader({ cash_discount_due_date: e.target.value })} />
          </label>
          <label className="sales-form-grid__wide">{t("sales:editor.fields.bankAccount")}
            <select value={header.bank_account_id} disabled={isReadOnly} onChange={(e) => updateHeader({ bank_account_id: e.target.value })}>
              <option value="">{t("sales:editor.placeholders.defaultBankAccount")}</option>
              {bankAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {[account.account_label, account.bank_name, account.iban || account.account_number].filter(Boolean).join(" — ")}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="sales-bank-preview">
          <DetailRow label={t("sales:editor.fields.bankName")} value={selectedBankAccount?.bank_name || activeSeller.seller_bank_name} />
          <DetailRow label={t("sales:editor.fields.ibanOrAccount")} value={selectedBankAccount?.iban || selectedBankAccount?.account_number || activeSeller.seller_iban || activeSeller.seller_bank_account_number} />
          <DetailRow label={t("sales:editor.fields.swiftBic")} value={selectedBankAccount?.swift_bic || activeSeller.seller_swift_bic} />
        </div>
        <div className="sales-discount-hint">
          <strong>{t("sales:editor.skontoPreviewTitle")}</strong>
          <span>{t("sales:editor.skontoPreviewText", {
            percent: header.cash_discount_percent || 0,
            date: header.cash_discount_due_date || "—",
            amount: formatMoney(cashDiscountAmount, header.currency),
            payable: formatMoney(cashDiscountPayableAmount, header.currency),
          })}</span>
        </div>
        <label className="sales-form-grid__wide sales-skonto-note">{t("sales:editor.fields.cashDiscountNote")}
          <textarea value={header.cash_discount_note} disabled={isReadOnly} onChange={(e) => updateHeader({ cash_discount_note: e.target.value })} rows="2" />
        </label>
      </section>

      {!isReadOnly && (
        <section
          className="sales-panel sales-add-item-panel"
          aria-label={t("sales:editor.sections.addProductOrService")}
        >
          {/*
            SalesItemPicker already renders its own visible label using
            catalog:picker.salesItemLabel. Keeping an additional section heading
            here makes the same text appear twice in the invoice editor.

            The section still keeps an aria-label so screen readers and future
            automated tests can identify the add-item area without duplicating
            visible UI text.
          */}
          <SalesItemPicker
            currency={header.currency}
            stockLocationId={header.stock_location_id}
            searchProducts={searchSalesProducts}
            searchServices={searchSalesServices}
            onSelectProduct={addProductLine}
            onSelectService={addServiceLine}
          />
        </section>
      )}

      <section className="sales-panel">
        <div className="sales-lines-header">
          <h2>{t("sales:editor.sections.lines")}</h2>
          {!isReadOnly && <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addManualLine}>{t("sales:editor.buttons.addManualLine")}</button>}
        </div>

        <div className="table-responsive">
          <table className="table table-sm align-middle sales-table sales-lines-table">
            <thead>
              <tr>
                <th>{t("sales:editor.fields.type")}</th>
                <th>{t("sales:editor.fields.name")}</th>
                <th className="text-end">{t("sales:editor.fields.quantity")}</th>
                <th>{t("sales:editor.fields.unit")}</th>
                <th className="text-end">{t("sales:editor.fields.grossUnit")}</th>
                <th className="text-end">{t("sales:editor.fields.vatPercent")}</th>
                <th className="text-end">{t("sales:editor.fields.discountPercent")}</th>
                <th className="text-end">{t("sales:editor.fields.discountGross")}</th>
                <th>{t("sales:editor.fields.stockLocation")}</th>
                <th className="text-end">{t("sales:editor.fields.net")}</th>
                <th className="text-end">{t("sales:editor.fields.vat")}</th>
                <th className="text-end">{t("sales:editor.fields.gross")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 && (
                <tr><td colSpan="13" className="text-center text-muted py-4">{t("sales:editor.emptyLines")}</td></tr>
              )}
              {lines.map((line, index) => {
                const preview = calculateLinePreview(line);
                const stockAvailability = line.line_type === "product" ? stockAvailabilityForLine(line) : null;
                return (
                  <tr key={line.local_id || index}>
                    <td>{t(`sales:lineTypes.${line.line_type}`, { defaultValue: line.line_type })}</td>
                    <td><input value={line.name} disabled={isReadOnly || line.line_type !== "manual"} onChange={(e) => updateLine(index, { name: e.target.value })} /></td>
                    <td className="text-end"><input className="sales-number-input" value={line.quantity} disabled={isReadOnly} onChange={(e) => updateLine(index, { quantity: e.target.value, stock_quantity: line.line_type === "product" ? e.target.value : line.stock_quantity })} /></td>
                    <td>{line.unit_code || "pcs"}</td>
                    <td className="text-end"><input className="sales-number-input" value={line.unit_price_gross} disabled={isReadOnly} onChange={(e) => updateLine(index, { unit_price_gross: e.target.value })} /></td>
                    <td className="text-end"><input className="sales-number-input" value={line.vat_rate_percent} disabled={isReadOnly} onChange={(e) => updateLine(index, { vat_rate_percent: e.target.value })} /></td>
                    <td className="text-end"><input className="sales-number-input" value={line.discount_percent || "0"} disabled={isReadOnly} onChange={(e) => updateLine(index, { discount_percent: e.target.value })} /></td>
                    <td className="text-end"><input className="sales-number-input" value={line.discount_amount_gross || "0"} disabled={isReadOnly} onChange={(e) => updateLine(index, { discount_amount_gross: e.target.value })} /></td>
                    <td className="sales-stock-location-cell">
                      {line.line_type === "product" ? (
                        <>
                          <select value={line.stock_location_id || header.stock_location_id || ""} disabled={isReadOnly} onChange={(e) => updateLine(index, { stock_location_id: e.target.value })}>
                            <option value="">{t("sales:editor.defaultStockLocation")}</option>
                            {locations.map((loc) => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
                          </select>
                          {!isReadOnly && stockAvailability && (
                            <div className={`sales-stock-availability ${stockAvailability.insufficient ? "sales-stock-availability--warning" : ""}`}>
                              <div>
                                <strong>{t("sales:editor.stockAvailability.availableNow")}</strong>
                                <span>{formatQuantity(stockAvailability.available, stockAvailability.unit)}</span>
                              </div>
                              <div>
                                <strong>{t("sales:editor.stockAvailability.remainingAfterPosting")}</strong>
                                <span>{formatQuantity(stockAvailability.remaining, stockAvailability.unit)}</span>
                              </div>
                              {stockAvailability.insufficient && (
                                <p>
                                  {t("sales:editor.stockAvailability.insufficient", {
                                    location: stockAvailability.locationName,
                                    available: formatQuantity(stockAvailability.available, stockAvailability.unit),
                                    required: formatQuantity(stockAvailability.requested, stockAvailability.unit),
                                  })}
                                </p>
                              )}
                            </div>
                          )}
                          {isReadOnly && (
                            <div className="sales-stock-posted-hint">
                              {t("sales:editor.stockAvailability.postedLocationOnly", { location: stockAvailability?.locationName || "—" })}
                            </div>
                          )}
                        </>
                      ) : <span className="sales-muted">{t("sales:editor.noStockIssue")}</span>}
                    </td>
                    <td className="text-end">{formatMoney(preview.net, header.currency)}</td>
                    <td className="text-end">{formatMoney(preview.vat, header.currency)}</td>
                    <td className="text-end">{formatMoney(preview.gross, header.currency)}</td>
                    <td className="text-end">
                      {!isReadOnly && <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeLine(index)}>{t("sales:editor.buttons.remove")}</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <div className="sales-grid">
        <section className="sales-panel">
          <h2>{t("sales:editor.sections.vatSummary")}</h2>
          <table className="table table-sm sales-table">
            <thead>
              <tr>
                <th>{t("sales:editor.fields.vatPercent")}</th>
                <th className="text-end">{t("sales:editor.fields.net")}</th>
                <th className="text-end">{t("sales:editor.fields.vat")}</th>
                <th className="text-end">{t("sales:editor.fields.gross")}</th>
                <th className="text-end">{t("sales:editor.fields.discountGross")}</th>
              </tr>
            </thead>
            <tbody>
              {vatSummary.length === 0 && <tr><td colSpan="5" className="text-muted">{t("sales:editor.emptyVatSummary")}</td></tr>}
              {vatSummary.map((row) => (
                <tr key={row.vat_rate_percent}>
                  <td>{row.vat_rate_percent.toFixed(2)}%</td>
                  <td className="text-end">{formatMoney(row.net, header.currency)}</td>
                  <td className="text-end">{formatMoney(row.vat, header.currency)}</td>
                  <td className="text-end">{formatMoney(row.gross, header.currency)}</td>
                  <td className="text-end">{formatMoney(row.discountGross, header.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="sales-panel">
          <h2>{t("sales:editor.sections.notes")}</h2>
          <div className="sales-form-grid">
            <label>{t("sales:editor.fields.printNote")}
              <textarea value={header.print_note} disabled={isReadOnly} onChange={(e) => updateHeader({ print_note: e.target.value })} rows="4" />
            </label>
            <label>{t("sales:editor.fields.internalNote")}
              <textarea value={header.internal_note} disabled={isReadOnly} onChange={(e) => updateHeader({ internal_note: e.target.value })} rows="4" />
            </label>
          </div>
        </section>
      </div>

      <section className="sales-panel sales-totals-panel">
        <div><span>{t("sales:editor.fields.grossBeforeDiscount")}</span><strong>{formatMoney(totals.grossBeforeDiscount, header.currency)}</strong></div>
        <div><span>{t("sales:editor.fields.discountGross")}</span><strong>{formatMoney(totals.discountGross, header.currency)}</strong></div>
        <div><span>{t("sales:editor.fields.net")}</span><strong>{formatMoney(totals.net, header.currency)}</strong></div>
        <div><span>{t("sales:editor.fields.vat")}</span><strong>{formatMoney(totals.vat, header.currency)}</strong></div>
        <div><span>{t("sales:editor.fields.gross")}</span><strong>{formatMoney(totals.gross, header.currency)}</strong></div>
        <div><span>{t("sales:editor.fields.cashDiscountAmount")}</span><strong>{formatMoney(cashDiscountAmount, header.currency)}</strong></div>
      </section>
    </div>
  );
};

export default SalesDocumentEditPage;


